import { parseLineString } from '@/services/lineStringService'
import type { RoutePoint, Track } from '@/types/tour'

/**
 * Squared distance from a point to a line segment (planar approximation,
 * sufficient for "pick nearest" semantics at tour scale).
 */
function distSqToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax
  const dy = by - ay
  if (dx === 0 && dy === 0) {
    const rx = px - ax
    const ry = py - ay
    return rx * rx + ry * ry
  }
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy)))
  const cx = ax + t * dx
  const cy = ay + t * dy
  const ex = px - cx
  const ey = py - cy
  return ex * ex + ey * ey
}

function nearestDistanceSqToPolyline(poly: [number, number][], lon: number, lat: number): number {
  if (poly.length === 0) return Number.POSITIVE_INFINITY
  if (poly.length === 1) {
    const dx = poly[0][0] - lon
    const dy = poly[0][1] - lat
    return dx * dx + dy * dy
  }
  let best = Number.POSITIVE_INFINITY
  for (let i = 1; i < poly.length; i++) {
    const d = distSqToSegment(lon, lat, poly[i - 1][0], poly[i - 1][1], poly[i][0], poly[i][1])
    if (d < best) best = d
  }
  return best
}

/**
 * Return the `clientId` of the track whose polyline is closest to the given
 * lat/lon, or null if no track has a usable polyline.
 */
export function findNearestTrackClientId(
  tracks: Pick<Track, 'clientId' | 'pointsLineReduced5m' | 'segments'>[],
  lat: number,
  lon: number,
): string | null {
  let bestId: string | null = null
  let bestDist = Number.POSITIVE_INFINITY
  for (const track of tracks) {
    const line = track.pointsLineReduced5m
    const poly = line ? parseLineString(line) : []
    if (poly.length === 0 && track.segments) {
      for (const seg of track.segments) {
        for (const p of seg.points ?? []) {
          poly.push([p.lon, p.lat])
        }
      }
    }
    if (poly.length === 0 || !track.clientId) continue
    const d = nearestDistanceSqToPolyline(poly, lon, lat)
    if (d < bestDist) {
      bestDist = d
      bestId = track.clientId
    }
  }
  return bestId
}

/**
 * Group route points per trackClientId (preserving array order per group).
 * RPs with the same `trackClientId` share a numbering sequence (A / 1 / … / B).
 */
export function groupRoutePointsByTrack<T extends Pick<RoutePoint, 'trackClientId'>>(
  routePoints: T[],
): Map<string | null, T[]> {
  const groups = new Map<string | null, T[]>()
  for (const rp of routePoints) {
    const key = rp.trackClientId ?? null
    const arr = groups.get(key)
    if (arr) arr.push(rp)
    else groups.set(key, [rp])
  }
  return groups
}

/**
 * Return the display label for a route point within its track group.
 * First in group → "A", last → "B" (if group has > 1 RP), others → sequential number.
 */
export function getRoutePointLabelInGroup(indexInGroup: number, groupSize: number): string {
  if (indexInGroup === 0) return 'A'
  if (indexInGroup === groupSize - 1 && groupSize > 1) return 'B'
  return String(indexInGroup)
}

/**
 * Start (A) / end (B) / intermediate route-point fill colours. Single source of truth shared by the
 * map markers (`routePointMarkerElement.ts`) and the route-point list (`RoutePointList.vue`) so the
 * two never drift — start reads green, end reads red, intermediates keep the route blue (Komoot-style
 * start/end distinction). Values are AA-legible behind bold white labels.
 */
export const ROUTE_POINT_COLORS = {
  start: '#15803D', // green-700
  end: '#DC2626', // red-600
  intermediate: '#3B82F6', // blue-500
} as const

/** Map a route-point display label ("A" / "B" / "1"…) to its marker fill colour. */
export function getRoutePointColor(label: string): string {
  if (label === 'A') return ROUTE_POINT_COLORS.start
  if (label === 'B') return ROUTE_POINT_COLORS.end
  return ROUTE_POINT_COLORS.intermediate
}

/**
 * Compute the per-track label for every route point of a tour in one pass.
 * Returns a map keyed by `RoutePoint.clientId`.
 */
export function computeRoutePointLabels<T extends Pick<RoutePoint, 'clientId' | 'trackClientId'>>(
  routePoints: T[],
): Map<string, string> {
  const labels = new Map<string, string>()
  const groupPos = new Map<string | null, number>()
  const groupSize = new Map<string | null, number>()
  for (const rp of routePoints) {
    const key = rp.trackClientId ?? null
    groupSize.set(key, (groupSize.get(key) ?? 0) + 1)
  }
  for (const rp of routePoints) {
    const key = rp.trackClientId ?? null
    const pos = groupPos.get(key) ?? 0
    const size = groupSize.get(key) ?? 1
    labels.set(rp.clientId, getRoutePointLabelInGroup(pos, size))
    groupPos.set(key, pos + 1)
  }
  return labels
}
